import { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { message } from 'antd';
import dayjs from 'dayjs';
import { CardType, CARD_CONFIGS, STATUS_LABELS } from './types';
import { inspectionService } from '@/services/inspection.service';
import { inspectionTypeService } from '@/services/inspectionType.service';
import { propertyTypeService } from '@/services/propertyType.service';
import { inspectionReasonService } from '@/services/inspectionReason.service';
import { providerCompanyService } from '@/services/providerCompany.service';
import { InspectionTableData, FilterableColumn } from '@/components/InspectionsTable/useInspectionsTable';
import { InspectionItemStatus } from '@/shared/enums/InspectionStatus';
import { getInspectionItemStatusInfo } from '@/shared/status';
import { useAuth } from '@/contexts/auth.context';
import { usePermissions } from '@/shared/hooks/usePermissions';

interface OptionItem { value: string; label: string; }

/** Estado dos filtros de coluna (server-side). IDs para os campos de cadastro. */
type ColumnFilters = Partial<Record<FilterableColumn, string | undefined>>;

function parseStatusToNumber(status: unknown): number {
  if (status === null || status === undefined) return 0;

  if (typeof status === 'number') return status;

  if (typeof status === 'string') {
    const parsed = parseInt(status, 10);
    if (!isNaN(parsed)) return parsed;
    const enumValue = InspectionItemStatus[status as keyof typeof InspectionItemStatus];
    if (typeof enumValue === 'number') return enumValue;
  }

  return 0;
}

export interface DetailFilters {
  searchCode?: string;
  startDate?: string;
  endDate?: string;
  status?: string;
  providerId?: string;
  inspectorId?: string;
  channel?: string;
  city?: string;
  inspectionType?: string;
  propertyType?: string;
  inspectionReason?: string;
  provider?: string;
}

export function useDashboardDetail() {
  const { cardType } = useParams<{ cardType: CardType }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { isProviderUser, isBankUser } = usePermissions();
  const isProvider = isProviderUser();
  const userProviderId = user?.providerId;

  const [isLoading, setIsLoading] = useState(false);
  const [inspections, setInspections] = useState<InspectionTableData[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Filtros de coluna (server-side). Para os campos de cadastro o valor é o Id.
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({});

  // Opções "de cadastro" (listas globais completas) para os campos bounded.
  const [inspectionTypeOptions, setInspectionTypeOptions] = useState<OptionItem[]>([]);
  const [propertyTypeOptions, setPropertyTypeOptions] = useState<OptionItem[]>([]);
  const [inspectionReasonOptions, setInspectionReasonOptions] = useState<OptionItem[]>([]);
  const [providerOptions, setProviderOptions] = useState<OptionItem[]>([]);

  const defaultStartDate = cardType === 'pending'
    ? undefined
    : searchParams.get('startDate') || dayjs().subtract(30, 'day').format('YYYY-MM-DD');

  const defaultEndDate = cardType === 'pending'
    ? undefined
    : searchParams.get('endDate') || dayjs().format('YYYY-MM-DD');

  const [filters, setFilters] = useState<DetailFilters>({
    startDate: defaultStartDate,
    endDate: defaultEndDate,
    status: searchParams.get('status') || undefined,
  });

  const cardConfig = cardType ? CARD_CONFIGS[cardType] : null;

  const PENDING_FINAL_STATUSES = [
    'LaudoAceitoPeloBanco',
    'Cancelado',
    'Frustrado',
    'TelevistoriaFinalizada',
    'LaudoAutomaticoGerado',
  ];

  const statusOptions = cardConfig?.statuses.length === 0
    ? Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label }))
    : cardType === 'pending'
      ? Object.entries(STATUS_LABELS)
          .filter(([key]) => !PENDING_FINAL_STATUSES.includes(key))
          .map(([value, label]) => ({ value, label }))
      : cardConfig?.statuses.map((status) => ({
          value: status,
          label: STATUS_LABELS[status] || status,
        })) || [];

  const loadItems = useCallback(async () => {
    if (!cardConfig) return;

    // Guard: se é provider mas não tem providerId ainda, não buscar (evita vazar dados de outras prestadoras)
    if (isProvider && !userProviderId) return;

    setIsLoading(true);
    try {
      // O filtro de status da coluna (enum) tem precedência sobre o da toolbar.
      const effectiveStatus = columnFilters.status || filters.status;
      const statusesToFilter = effectiveStatus
        ? [effectiveStatus]
        : cardConfig.statuses.length > 0
          ? cardConfig.statuses
          : undefined;

      const hasOpenIssues = cardType === 'pending' && !effectiveStatus ? true : undefined;

      const response = await inspectionService.getInspections({
        page: currentPage,
        pageSize,
        statuses: statusesToFilter,
        startDate: filters.startDate,
        endDate: filters.endDate,
        searchCode: filters.searchCode,
        // Prestadora sempre travada no próprio id; senão, filtro de coluna ou toolbar.
        providerId: (isProvider && userProviderId)
          ? userProviderId
          : (columnFilters.provider || filters.providerId),
        inspectorId: filters.inspectorId,
        hasOpenIssues,
        // Filtros de coluna server-side (geral, não só a página atual).
        inspectionTypeId: columnFilters.inspectionType,
        propertyTypeId: columnFilters.propertyType,
        inspectionReasonId: columnFilters.inspectionReason,
        city: columnFilters.city,
      });

      const mappedInspections = response.items.map((item: any) => {
        const statusNumber = parseStatusToNumber(item.status);

        const statusInfo = getInspectionItemStatusInfo(statusNumber);
        const statusDescription = (item.statusLabel && item.statusLabel.trim() !== '')
          ? item.statusLabel
          : (item.statusDescription && item.statusDescription.trim() !== '')
            ? item.statusDescription
            : statusInfo.label;

        return {
          id: item.inspectionRequestId || item.id,
          key: item.id,
          proposalNumber: item.proposalNumber || item.inspectionRequest?.proposalNumber || '-',
          squad: item.squad || item.inspectionRequest?.squad || null,
          proponents: item.proponents?.map((p: any) => p.name).join(', ') ||
                      item.inspectionRequest?.proponents?.map((p: any) => p.name).join(', ') || '-',
          channel: item.channel || item.inspectionRequest?.channel || '-',
          status: String(item.status),
          createdAt: item.createdAt,
          isFavorite: item.isFavorite || false,
          items: [{
            id: item.id,
            inspectionReasonId: item.inspectionReasonId || '',
            inspectionReasonName: item.inspectionReasonName || '',
            inspectionTypeId: item.inspectionTypeId || '',
            inspectionTypeName: item.inspectionTypeName || '',
            propertyTypeId: item.propertyTypeId || '',
            propertyTypeName: item.propertyTypeName || '',
            cep: item.cep || '',
            street: item.street || '-',
            number: item.number || '',
            complement: item.complement || '',
            district: item.district || '',
            city: item.city || '-',
            state: item.state || '-',
            status: statusNumber,
            statusDescription: statusDescription,
            assignments: item.assignments || [],
            issues: item.issues || [],
          }],
          itemCount: 1,
          assignmentId: item.assignments?.[0]?.id,
          inspectionRequestId: item.inspectionRequestId || item.id,
        };
      }) as InspectionTableData[];

      setInspections(mappedInspections);
      setTotalItems(response.totalCount);
    } catch {
      setInspections([]);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  }, [cardConfig, currentPage, pageSize, filters, columnFilters, isProvider, userProviderId]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // Opções "de cadastro" (listas globais completas) — carregadas uma vez.
  // São independentes da página atual, então o filtro é realmente geral.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [types, propTypes, reasons, providers] = await Promise.all([
          inspectionTypeService.getAllInspectionTypes().catch(() => []),
          propertyTypeService.getAllPropertyTypes().catch(() => []),
          inspectionReasonService.getAll().catch(() => []),
          providerCompanyService.getAllProviders().catch(() => []),
        ]);
        if (cancelled) return;
        const toOpt = (arr: { id: string; name: string }[]) =>
          arr.map((x) => ({ value: x.id, label: x.name })).sort((a, b) => a.label.localeCompare(b.label));
        setInspectionTypeOptions(toOpt(types as { id: string; name: string }[]));
        setPropertyTypeOptions(toOpt(propTypes as { id: string; name: string }[]));
        setInspectionReasonOptions(toOpt(reasons as { id: string; name: string }[]));
        setProviderOptions(toOpt(providers as { id: string; name: string }[]));
      } catch {
        // silencioso: filtros sem opções continuam usáveis (busca vazia)
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Carregador paginado para campos de texto livre (cidade) — distinct global.
  const loadFilterOptions = useCallback(
    async (field: 'city', params: { search: string; pageNumber: number; pageSize: number }) => {
      const statusesToFilter = columnFilters.status
        ? [columnFilters.status]
        : cardConfig && cardConfig.statuses.length > 0
          ? cardConfig.statuses
          : undefined;
      return inspectionService.getFilterOptions({
        field,
        search: params.search,
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
        statuses: statusesToFilter,
        providerId: (isProvider && userProviderId) ? userProviderId : columnFilters.provider,
      });
    },
    [columnFilters.status, columnFilters.provider, cardConfig, isProvider, userProviderId]
  );

  const handleColumnFilterChange = useCallback((field: FilterableColumn, value: string | undefined) => {
    setColumnFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  }, []);

  const handleFilterChange = useCallback((field: keyof DetailFilters, value: string | undefined) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({
      startDate: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
      endDate: dayjs().format('YYYY-MM-DD'),
    });
    setColumnFilters({});
    setCurrentPage(1);
    message.info('Filtros limpos');
  }, []);

  const handleApplyFilters = useCallback(() => {
    loadItems();
    message.success('Filtros aplicados');
  }, [loadItems]);

  const handlePageChange = useCallback((page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  }, []);

  const handleGoBack = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const handleToggleFavorite = useCallback(async (inspectionId: string) => {
    // Apenas usuários do banco podem editar prioridades. Guard defensivo
    // (a UI já oculta o clique para outras camadas, e o backend retorna 403).
    if (!isBankUser()) {
      return;
    }
    try {
      const result = await inspectionService.toggleFavorite(inspectionId);
      message.success(result.isFavorite ? 'Adicionado aos favoritos' : 'Removido dos favoritos');
      await loadItems();
    } catch {
      message.error('Erro ao alterar favorito');
    }
  }, [loadItems, isBankUser]);

  return {
    cardType,
    cardConfig,
    inspections,
    totalItems,
    currentPage,
    pageSize,
    isLoading,
    filters,
    statusOptions,
    // Opções de cadastro (globais) + estado/handler dos filtros de coluna server-side.
    inspectionTypeOptions,
    propertyTypeOptions,
    inspectionReasonOptions,
    providerOptions,
    columnFilters,
    onColumnFilterChange: handleColumnFilterChange,
    loadFilterOptions,
    handleFilterChange,
    handleClearFilters,
    handleApplyFilters,
    handlePageChange,
    handleGoBack,
    handleToggleFavorite,
    refreshData: loadItems,
  };
}
